// Phone worklist: the arrears cases as cards, filters in a sheet, long-press / Select for bulk actions. Same state + API as the desktop table.
import { useRef, useState } from "react";
import { AlarmClock, CheckSquare, Filter, Loader2, X, CalendarClock, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { JourneyChips } from "@/components/collections/JourneyLine";
import { StatusTagChips } from "@/components/collections/StatusTags";
import { TriagePills, rowPills } from "@/components/collections/TriagePills";
import { HandToLegalButton } from "@/components/collections/JourneyActions";
import { BottomSheet, PullToRefresh } from "@/components/phone/PhoneBits";
import { fmtMoney, rl } from "@/lib/regionFmt";
import { cn } from "@/lib/utils";
import { PhoneWorklistFilters, activeFilterCount } from "./PhoneWorklistFilters";

const fmtDate = (iso) => (iso ? new Date(iso.length > 10 ? iso : `${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "");

function useLongPress(onLong, ms = 480) {
  const timer = useRef(null);
  const fired = useRef(false);
  const start = (e) => { fired.current = false; timer.current = setTimeout(() => { fired.current = true; onLong(e); }, ms); };
  const cancel = () => { if (timer.current) clearTimeout(timer.current); timer.current = null; };
  return { onTouchStart: start, onTouchEnd: cancel, onTouchMove: cancel, onTouchCancel: cancel, onContextMenu: (e) => { e.preventDefault(); onLong(e); }, fired };
}

function CaseCard({ c, stageMeta, stageTones, dpdTone, accountStatuses, dot, selectMode, selected, onOpen, onToggle, onLongPress, reload }) {
  const lp = useLongPress(onLongPress);
  const j = c.journey;
  const na = j?.next_action;
  const hasPills = rowPills(c).length > 0;
  const director = c.borrower_director_name && c.borrower_director_name !== c.customer_name ? c.borrower_director_name : null;
  const click = () => { if (lp.fired.current) { lp.fired.current = false; return; } if (selectMode) onToggle(); else onOpen(); };
  return (
    <div role="button" tabIndex={0} onClick={click} onKeyDown={(e) => e.key === "Enter" && click()}
      onTouchStart={lp.onTouchStart} onTouchEnd={lp.onTouchEnd} onTouchMove={lp.onTouchMove} onTouchCancel={lp.onTouchCancel} onContextMenu={lp.onContextMenu}
      className={cn("rounded-xl border bg-white px-3 py-2.5 shadow-sm active:bg-slate-50 sf-tap select-none", selected && "border-indigo-400 bg-indigo-50/40")}
      data-testid={`phone-case-card-${c.case_id}`}>
      <div className="flex items-start gap-2">
        {selectMode && <Checkbox checked={selected} className="h-5 w-5 mt-0.5 pointer-events-none" aria-label={`Select ${c.customer_name}`} data-testid={`phone-case-select-${c.case_id}`} />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            {dot}
            <span className="font-semibold text-[15px] leading-tight truncate" data-testid={`phone-case-name-${c.case_id}`}>{c.customer_name || "—"}</span>
          </div>
          <div className="text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap mt-0.5">
            <span className="font-mono">{c.loan_application_id || "—"}</span>
            {director && <span className="truncate">· {director}</span>}
          </div>
        </div>
        <span className={cn("shrink-0 inline-flex items-center justify-center min-w-[46px] px-2 py-0.5 rounded-full text-xs font-bold tabular-nums", dpdTone(c.days_past_due))} data-testid={`phone-case-dpd-${c.case_id}`}>
          {c.days_past_due ?? "—"}<span className="ml-0.5 font-medium opacity-70 text-[10px]">d</span>
        </span>
      </div>

      <div className="mt-2 flex items-end justify-between gap-2">
        <div className="flex items-baseline gap-3">
          <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Arrears</div><div className="text-sm font-semibold text-rose-700 tabular-nums" data-testid={`phone-case-arrears-${c.case_id}`}>{fmtMoney(c.arrears_amount || 0, 0)}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Balance</div><div className="text-sm font-semibold text-slate-900 tabular-nums">{fmtMoney(c.balance_amount || 0, 0)}</div></div>
        </div>
        <Badge variant="outline" className={`text-[10px] ${stageTones[c.stage] || ""}`}>{stageMeta[c.stage]?.label || c.stage || "—"}</Badge>
      </div>

      {(j || c.breathing_space?.active || c.manual_statuses?.length > 0 || hasPills) && (
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          {j && <JourneyChips journey={j} caseId={c.case_id} />}
          {c.breathing_space?.active && <Badge className="bg-purple-600 text-white border-purple-700 text-[9px] font-bold tracking-wider px-1.5 h-4">⏸ {rl("hold").toUpperCase()}</Badge>}
          {c.manual_statuses?.length > 0 && <StatusTagChips keys={c.manual_statuses} statuses={accountStatuses} testid="phone-case-status-tag" />}
          {hasPills && <TriagePills row={c} />}
        </div>
      )}
      {j?.sub && <div className="mt-1 text-[11px] text-slate-600 line-clamp-2" data-testid={`phone-case-journey-sub-${c.case_id}`}>{j.sub}</div>}
      {j?.attention?.length > 0 && <div className="mt-0.5 text-[11px] text-amber-700">⚠ {j.attention[0].label}{j.attention.length > 1 ? ` +${j.attention.length - 1}` : ""}</div>}

      <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-x-3 gap-y-1 flex-wrap text-[11px] text-slate-600">
        {na ? (
          <span className="inline-flex items-center gap-1 min-w-0" data-testid={`phone-case-next-${c.case_id}`}>
            <CalendarClock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="truncate">{na.label}</span>
            <span className={cn("tabular-nums shrink-0", na.overdue_days > 0 ? "text-red-700 font-semibold" : "")}>· {fmtDate(na.date)}{na.overdue_days > 0 ? ` · ${na.overdue_days}d late` : ""}</span>
          </span>
        ) : <span className="italic text-slate-400">nothing diarised</span>}
        {j?.promise && <span className="inline-flex items-center gap-1" data-testid={`phone-case-promise-${c.case_id}`}><Target className="w-3.5 h-3.5 text-emerald-600" /> {j.promise.label}</span>}
        <span className="ml-auto shrink-0">{c.handler ? <span className="font-medium text-slate-700">{c.handler}</span> : <span className="italic text-slate-400">Unassigned</span>}</span>
      </div>
      {["ready_for_handover", "pre_action"].includes(j?.legal?.key) && !selectMode && (
        <div className="mt-2" onClick={(e) => e.stopPropagation()}><HandToLegalButton caseRow={c} journey={j} onDone={reload} /></div>
      )}
    </div>
  );
}

export default function PhoneWorklist(props) {
  const { cases, totalCount, loading, reload, filter, setFilter, sort, setSort, handlers, stages, stageMeta, stageTones, dpdTone, dpdBuckets, contactability, accountStatuses,
    latestSnapshot, snapshotMeta, selectedIds, toggleRow, clearSelection, handleBulkAssign, requestBulkStage, bulkStagePending, cancelBulkStage, confirmBulkStage, bulkBusy, openCase, offListMatches, ContactabilityDot } = props;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const nActive = activeFilterCount(filter);
  const exitSelect = () => { setSelectMode(false); clearSelection(); };

  const chips = [
    filter.search && { k: "search", label: `“${filter.search}”` },
    filter.handler && { k: "handler", label: filter.handler.split(",").filter(Boolean).join(", ") },
    filter.stage && { k: "stage", label: stageMeta[filter.stage]?.label || filter.stage },
    filter.dpd_bucket && { k: "dpd_bucket", label: dpdBuckets.find((b) => b.key === filter.dpd_bucket)?.label || filter.dpd_bucket },
    filter.manual_status && { k: "manual_status", label: accountStatuses.find((s) => s.key === filter.manual_status)?.label || filter.manual_status },
    filter.capital_at_risk && { k: "capital_at_risk", label: "Capital at Risk" },
    filter.bs_protected && { k: "bs_protected", label: rl("hold") },
  ].filter(Boolean);

  return (
    <div className="space-y-3" data-testid="phone-worklist">
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5"><AlarmClock className="w-4 h-4 text-amber-600" /><h1 className="text-base font-semibold truncate">Loan Arrears Ageing</h1></div>
          <div className="text-[11px] text-slate-500 truncate" data-testid="phone-worklist-meta">
            {latestSnapshot ? <>{latestSnapshot.row_count ?? totalCount} cases · {fmtMoney(latestSnapshot.arrears_amount || 0, 0)} arrears{snapshotMeta?.rel ? ` · report ${snapshotMeta.rel}` : ""}</> : "No arrears report uploaded yet"}
          </div>
        </div>
        <Button variant={selectMode ? "default" : "outline"} size="sm" className="h-10 px-3" onClick={() => (selectMode ? exitSelect() : setSelectMode(true))} data-testid="phone-select-toggle">
          <CheckSquare className="w-4 h-4 mr-1" /> {selectMode ? "Done" : "Select"}
        </Button>
        <Button variant="outline" size="sm" className="h-10 px-3 relative" onClick={() => setFiltersOpen(true)} data-testid="phone-filter-btn">
          <Filter className="w-4 h-4 mr-1" /> Filter
          {nActive > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center" data-testid="phone-filter-count">{nActive}</span>}
        </Button>
      </div>

      {chips.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto sf-no-scrollbar -mx-3 px-3" data-testid="phone-active-filters">
          {chips.map((ch) => (
            <button key={ch.k} type="button" onClick={() => setFilter((f) => ({ ...f, [ch.k]: "" }))} className="h-8 px-2.5 rounded-full bg-slate-900 text-white text-[12px] inline-flex items-center gap-1 whitespace-nowrap sf-tap" data-testid={`phone-active-filter-${ch.k}`}>
              {ch.label} <X className="w-3.5 h-3.5" />
            </button>
          ))}
        </div>
      )}

      <div className="text-[11px] text-slate-500 px-0.5" data-testid="phone-worklist-showing">Showing <span className="font-semibold text-slate-700">{cases.length}</span> of {totalCount}{selectMode && selectedIds.size > 0 ? ` · ${selectedIds.size} selected` : ""}</div>

      {offListMatches.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1" data-testid="phone-offlist">
          <div className="font-semibold">Not on today's worklist — notes and history are on the file:</div>
          {offListMatches.map((m) => (
            <button key={m.case_id} type="button" onClick={() => openCase(m)} className="block w-full text-left h-9 truncate sf-tap" data-testid={`phone-offlist-${m.case_id}`}>
              <span className="font-semibold">{m.customer_name}</span> <span className="font-mono">{m.loan_application_id}</span> {m.held_in_legal ? "· ⚖ held in Legal" : "· off list"} →
            </button>
          ))}
        </div>
      )}

      <PullToRefresh onRefresh={reload}>
        {loading && cases.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading cases…</div>
        ) : cases.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500 space-y-2" data-testid="phone-worklist-empty">
            <div>No cases match these filters.</div>
            {nActive > 0 && <Button variant="outline" size="sm" onClick={() => setFilter({ search: "", handler: "", stage: "", dpd_bucket: "", manual_status: "", capital_at_risk: "", bs_protected: "" })}>Clear filters</Button>}
          </div>
        ) : (
          <div className="space-y-2 pb-24" data-testid="phone-case-list">
            {cases.map((c) => (
              <CaseCard key={c.case_id} c={c} stageMeta={stageMeta} stageTones={stageTones} dpdTone={dpdTone} accountStatuses={accountStatuses}
                dot={<ContactabilityDot dot={contactability[c.case_id]} />} selectMode={selectMode} selected={selectedIds.has(c.case_id)}
                onOpen={() => openCase(c)} onToggle={() => toggleRow(c.case_id)} onLongPress={() => { setSelectMode(true); if (!selectedIds.has(c.case_id)) toggleRow(c.case_id); }} reload={reload} />
            ))}
          </div>
        )}
      </PullToRefresh>

      {selectMode && (
        <div className="fixed inset-x-0 z-40 bg-indigo-50 border-t-2 border-indigo-200 px-3 py-2 space-y-2" style={{ bottom: "calc(4rem + env(safe-area-inset-bottom, 0px))" }} data-testid="phone-bulk-bar">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold text-indigo-900">{selectedIds.size} selected</span>
            <button type="button" className="text-xs text-indigo-800 underline h-8 px-2 sf-tap" onClick={exitSelect} data-testid="phone-bulk-clear">Clear</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select disabled={bulkBusy || selectedIds.size === 0} onValueChange={(v) => handleBulkAssign(v === "_unassign" ? null : v)}>
              <SelectTrigger className="h-11 bg-white text-sm" data-testid="phone-bulk-assign"><SelectValue placeholder={bulkBusy ? "Assigning…" : "Assign to…"} /></SelectTrigger>
              <SelectContent>
                {handlers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                <SelectItem value="_unassign">— Unassign —</SelectItem>
              </SelectContent>
            </Select>
            <Select disabled={bulkBusy || selectedIds.size === 0} value="" onValueChange={requestBulkStage}>
              <SelectTrigger className="h-11 bg-white text-sm" data-testid="phone-bulk-stage"><SelectValue placeholder="Move to stage…" /></SelectTrigger>
              <SelectContent>{stages.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      )}

      <PhoneWorklistFilters open={filtersOpen} onClose={() => setFiltersOpen(false)} filter={filter} setFilter={setFilter} sort={sort} setSort={setSort}
        handlers={handlers} stages={stages} byStage={props.byStage} byHandler={props.byHandler} byDpdBucket={props.byDpdBucket} dpdBuckets={dpdBuckets}
        accountStatuses={accountStatuses} capitalAtRiskCount={props.capitalAtRiskCount} bsProtectedCount={props.bsProtectedCount} resultCount={cases.length} />

      <BottomSheet open={!!bulkStagePending} onClose={cancelBulkStage} title={`Move ${bulkStagePending?.count} case${bulkStagePending?.count === 1 ? "" : "s"} → ${stageMeta[bulkStagePending?.stage]?.label || bulkStagePending?.stage}?`} testid="phone-bulk-stage-confirm"
        footer={(
          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="h-11 flex-1" onClick={cancelBulkStage} data-testid="phone-bulk-stage-cancel">Cancel</Button>
            <Button className={cn("h-11 flex-1", bulkStagePending?.stage === "legal" && "bg-red-600 hover:bg-red-700")} disabled={bulkBusy} onClick={confirmBulkStage} data-testid="phone-bulk-stage-confirm-btn">{bulkBusy ? "Updating…" : "Confirm"}</Button>
          </div>
        )}>
        {bulkStagePending?.stage === "legal" && <div className="rounded border border-red-200 bg-red-50 p-3 text-red-800 text-sm"><div className="font-semibold mb-1">⚠️ This escalates each case on LoanDisk</div><div className="text-xs">Each loan's status will be set to 3 (Legal). Cases without a LoanDisk loan are skipped.</div></div>}
        {bulkStagePending?.stage === "open_loan" && <div className="rounded border border-amber-200 bg-amber-50 p-3 text-amber-900 text-sm"><div className="font-semibold mb-1">This sets each case back to Open on LoanDisk</div><div className="text-xs">Each loan's status will be set to 1 (Open).</div></div>}
      </BottomSheet>
    </div>
  );
}
